import PropTypes from 'prop-types'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useRemoveSubscriptionMutation } from 'api/subscriptions'
import { userTimeZoneFormat } from 'utils/time'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'
import * as sessionUserSelector from 'selectors/sessionUser'

import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import TableCellAction from 'components/Shared/TableNew/TableCellAction'
import Tooltip from 'components/Shared/Tooltip/Index'
import PartnersList from 'components/Shared/PartnersList/Index'
import Badge from 'components/Shared/Badge/Index'

import { RouteTicketingSystemsTicket } from 'routes'

const TicketsSubscriptionsTable = ({ subscriptions }) => {
  const { t } = useTranslation()
  const ticketingSystemsDisplayIds = useSelector(ticketingSystemsSelector.ticketingSystemsDisplayIds)

  const [removeSubscription] = useRemoveSubscriptionMutation()

  const ticketBondingEnabled = subscriptions.filter(i => i.tsTicketBonding === 'enabled')
  const showPartnerTicketId = ticketBondingEnabled.length > 0
  const tenantTicketIdFieldName = useSelector(sessionUserSelector.tenantTicketIdFieldName)
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const unsubscribe = (subscription) => {
    removeSubscription({
      ticketingSystemDisplayId: subscription.ticketingSystemDisplayId,
      ticketId: subscription.ticketingSystemExternalId,
    })
  }

  return (
    <>
      <Table width='100%'>
        <TableHead withTopBorder>
          <TableRow>
            <TableCell>{t('subscriptionsPage.tableHeaderId')}</TableCell>
            {showPartnerTicketId &&
              <TableCell>{tenantTicketIdFieldName.replaceAll('_', ' ')}</TableCell>}
            <TableCell>{t('subscriptionsPage.tableHeaderStatus')}</TableCell>
            <TableCell>{t('subscriptionsPage.tableHeaderTitle')}</TableCell>
            <TableCell>{t('subscriptionsPage.tableHeaderUpdated')}</TableCell>
            <TableCell>{t('subscriptionsPage.subscriptionCreatedAt')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {subscriptions.map((subscription, key) => {
            const isActiveTicketingSystem = ticketingSystemsDisplayIds.indexOf(subscription.ticketingSystemDisplayId) !== -1
            const statusName = subscription.ticketStatus
            const statusColor = subscription.ticketStatusColor
            const ticketUpdatedAt = userTimeZoneFormat(userTimeZone, subscription.ticketUpdatedAt)
            const createdAt = userTimeZoneFormat(userTimeZone, subscription.createdAt)

            let ticketUrl = null
            if (isActiveTicketingSystem) {
              ticketUrl = RouteTicketingSystemsTicket({
                ticketingSystemDisplayId: subscription.ticketingSystemDisplayId,
                ticketId: subscription.ticketingSystemExternalId,
              })
            }

            return (
              <TableRow
                key={subscription.ticketId}
                color={statusColor}
              >
                <TableCell>
                  {subscription.ticketingSystemExternalId}
                </TableCell>

                {showPartnerTicketId && (
                  <TableCell>
                    <PartnersList partnerIds={subscription.partnerTicketIdValue} defaultValue='' />
                  </TableCell>
                )}

                <TableCell noPadding>
                  <Badge big color={statusColor}>
                    {statusName}
                  </Badge>
                </TableCell>

                <TableCell>
                  {isActiveTicketingSystem && (
                    <Link to={ticketUrl}>
                      {subscription.ticketTitle}
                    </Link>
                  )}
                  {!isActiveTicketingSystem && (
                    <span
                      data-tooltip-content={t('subscriptionsPage.inactiveTooltip')}
                      data-tooltip-id='title-tooltip'
                    >
                      {subscription.ticketTitle}
                    </span>
                  )}
                  <Tooltip id='title-tooltip' key={key} />
                </TableCell>

                <TableCell>
                  {ticketUpdatedAt}
                </TableCell>

                <TableCell>
                  {createdAt}
                </TableCell>

                <TableCellWithActions>
                  <TableCellAction
                    iconName='icon-remove-16'
                    onClick={
                      () => unsubscribe(subscription)
                    }
                    tooltip={t('subscriptionsPage.unsubscribeTooltip')}
                    tooltipId={'col-tooltip' + key}
                  />
                  <Tooltip id={'col-tooltip' + key} />
                </TableCellWithActions>
              </TableRow>
            )
          })}
          <TableRowNoData data={subscriptions} />
        </TableBody>
      </Table>
    </>
  )
}

TicketsSubscriptionsTable.propTypes = {
  subscriptions: PropTypes.array.isRequired,
}

export default TicketsSubscriptionsTable
